import {useEffect, useRef, useState} from "react";
import {companyCreatePath, profileWorkspacesPath} from "@/app/paths";
import {LastOpenedCompanyKey} from "@/app/components/RememberCompany";
import {useOwnCompanies, useOwnInvites, useUser} from "@/hooks/queries/use-users";
import {useRouter, usePathname} from "next/navigation";
import {useUserRoles} from "@/hooks/use-user-roles";
import {useSession} from "next-auth/react";

export interface UseLastCompanyProps {
  pathProvider(companyId: number): string
}

export function useRedirectHandler({pathProvider}: UseLastCompanyProps) {
  const userCompanies = useOwnCompanies()
  const ownInvites = useOwnInvites()
  const {update} = useSession()
  const redirectTriggered = useRef(false)
  const router = useRouter()
  const pathname = usePathname()
  const {user, reload} = useUser()
  const trialStarted = user?.trialStarted
  const {canCreateCompany, loaded: userLoaded} = useUserRoles()
  const [reloadCount, setReloadCount] = useState(0)
  const workspacesRedirectCheck = useRef(false)
  useEffect(() => {
    if (!user || redirectTriggered.current) {
      return
    }
    if (userLoaded && (userCompanies?.length || 0) === 0) {
      // Check for pending invites first - redirect to workspaces page if user has invites
      if (!workspacesRedirectCheck.current) {
        workspacesRedirectCheck.current = true
        if (ownInvites.length > 0) {
          // Guard: don't redirect if already on workspaces page
          if (pathname !== profileWorkspacesPath()) {
            redirectTriggered.current = true
            router.push(profileWorkspacesPath())
          }
          return
        }
      }

      if (canCreateCompany) {
        redirectTriggered.current = true
        router.push(companyCreatePath())
      } else {
        if (!trialStarted && reloadCount < 5) {
          setTimeout(async () => {
            if (!canCreateCompany) {
              await reload()
              await update()
              setReloadCount(c => c + 1)
            }
          }, 1000)
        }

      }
      return
    }
    const lastOpenedCompany = localStorage.getItem(LastOpenedCompanyKey)
    const lastCompany = userCompanies?.find((company) => `${company.id}` === lastOpenedCompany)
    if (lastCompany) {
      router.push(pathProvider(lastCompany.id))
      return
    }
    if (userCompanies && userCompanies.length > 0) {
      router.push(pathProvider(userCompanies[0].id))
      return
    }
  }, [userCompanies, ownInvites, router, pathname, userLoaded, canCreateCompany, pathProvider, reloadCount, reload, trialStarted, update, user])
}
